import { useState, useEffect } from 'react';
import { User, Session, AuthChangeEvent } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { lovable } from '@/integrations/lovable';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);

  useEffect(() => {
    // Detect OAuth callback return
    try {
      const trace = sessionStorage.getItem('google_oauth_trace');
      const url = new URL(window.location.href);
      const hasOAuthParams = url.searchParams.has('code') || url.hash.includes('access_token') || url.searchParams.has('error');
      if (trace || hasOAuthParams) {
        console.group('[GoogleAuth] Retorno do callback OAuth');
        console.log('[GoogleAuth] trace:', trace);
        console.log('[GoogleAuth] url:', window.location.href);
        console.log('[GoogleAuth] searchParams:', Object.fromEntries(url.searchParams.entries()));
        console.log('[GoogleAuth] hash:', url.hash);
        const errParam = url.searchParams.get('error') || url.searchParams.get('error_description');
        if (errParam) console.error('[GoogleAuth] erro retornado pelo provider:', errParam);
        console.groupEnd();
        sessionStorage.removeItem('google_oauth_trace');
      }
    } catch (e) {
      console.warn('[GoogleAuth] falha ao inspecionar callback:', e);
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event: AuthChangeEvent, session) => {
        console.log('[Auth] onAuthStateChange:', event, { hasSession: !!session, userId: session?.user?.id, email: session?.user?.email });
        setSession(session);
        setUser(session?.user ?? null);
        setIsLoading(false);

        if (event === 'PASSWORD_RECOVERY') {
          setIsRecoveryMode(true);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) console.error('[Auth] getSession error:', error);
      console.log('[Auth] getSession resolved:', { hasSession: !!session, userId: session?.user?.id });
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/app`;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: redirectUrl },
    });
    if (error) throw error;
    // Best-effort analytics — wait for session before tracking.
    setTimeout(async () => {
      try {
        const { trackEvent } = await import('@/lib/analytics');
        await trackEvent('signup_completed', { email });
      } catch {}
    }, 1500);
  };

  const signInWithGoogle = async () => {
    const traceId = `gauth-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    // Canonical origin: always use https://fiscalonne.online in production hosts
    // to avoid mixing sessions between apex / www / lovable subdomain.
    const host = window.location.hostname;
    const isProdHost = [
      "fiscalonne.online",
      "www.fiscalonne.online",
      "xml-magic-convert.lovable.app",
    ].includes(host);
    const canonicalOrigin = isProdHost
      ? "https://www.fiscalonne.online"
      : window.location.origin;
    const redirectUri = `${canonicalOrigin}/app`;

    console.group(`[GoogleAuth ${traceId}] signInWithGoogle iniciado`);
    console.log('[GoogleAuth] origin:', window.location.origin);
    console.log('[GoogleAuth] canonicalOrigin:', canonicalOrigin);
    console.log('[GoogleAuth] href:', window.location.href);
    console.log('[GoogleAuth] redirect_uri:', redirectUri);
    console.log('[GoogleAuth] userAgent:', navigator.userAgent);
    console.log('[GoogleAuth] online:', navigator.onLine);
    console.log('[GoogleAuth] timestamp:', new Date().toISOString());
    try {
      sessionStorage.setItem('google_oauth_trace', JSON.stringify({ traceId, redirectUri, startedAt: Date.now() }));
    } catch {}

    const t0 = performance.now();
    try {
      const result = await lovable.auth.signInWithOAuth('google', { redirect_uri: redirectUri });
      const dt = (performance.now() - t0).toFixed(1);
      console.log(`[GoogleAuth ${traceId}] resposta em ${dt}ms`, {
        redirected: (result as any)?.redirected,
        hasError: !!(result as any)?.error,
        hasTokens: !!(result as any)?.tokens,
      });
      if ((result as any)?.redirected) {
        console.warn(`[GoogleAuth ${traceId}] Browser será redirecionado para Google. Se aparecer "Site perigoso", o bloqueio é do Google Safe Browsing no domínio.`);
      }
      if (result.error) {
        console.error(`[GoogleAuth ${traceId}] ERROR no fluxo OAuth:`, result.error);
        console.groupEnd();
        throw result.error;
      }
      console.log(`[GoogleAuth ${traceId}] sucesso — sessão estabelecida`);
      console.groupEnd();
    } catch (err: any) {
      console.error(`[GoogleAuth ${traceId}] exceção:`, err?.message || err, err);
      console.groupEnd();
      throw err;
    }
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const resetPassword = async (email: string) => {
    const redirectTo = `${window.location.origin}/app`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    if (error) throw error;
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    if (!user?.email) throw new Error('Usuário sem e-mail');
    // Re-autenticar
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: user.email,
      password: currentPassword,
    });
    if (signInError) throw new Error('Senha atual incorreta');
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
  };

  const clearRecoveryMode = () => setIsRecoveryMode(false);

  return {
    user,
    session,
    isLoading,
    isRecoveryMode,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    resetPassword,
    changePassword,
    clearRecoveryMode,
  };
}
